import { Capacitor } from '@capacitor/core';
import { Lock, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreditPurchaseModal } from '../../../components/CreditSystem';
import { useBillingPlanCards } from '../../../config/useBillingPlanCards';
import { getPackById } from '../../../config/creditPacks';
import { usePlanStatus } from '../../../hooks/usePlanStatus';
import type { PlanStatus } from '../../../hooks/usePlanStatus';
import { goToCreditCheckout } from '../../../utils/creditCheckoutNavigation';

function isCurrentBillingPlan(cardId: string, packId: string | null, plan: PlanStatus): boolean {
  if (cardId === 'free') {
    return !plan.planId || plan.planKey === 'free';
  }
  return plan.isActive && plan.planId === packId;
}

function BillingPlanCards({
  planStatus,
  onSelectPlan,
  cards,
}: {
  planStatus: PlanStatus;
  onSelectPlan: (packId: string) => void;
  cards: ReturnType<typeof useBillingPlanCards>;
}) {
  const { t } = useTranslation();

  return (
    <section className="billing-plans-section">
      <div className="billing-plans-grid">
        {cards.map((card) => {
          const isCurrent = isCurrentBillingPlan(card.id, card.packId, planStatus);
          const pack = card.packId ? getPackById(card.packId) : null;
          const FeatureIcon = card.planIcon ?? card.features[0]?.Icon ?? Zap;

          const ctaLabel = isCurrent
            ? card.id === 'free'
              ? t('views.accountSettings.billing.freePlanActive', 'Free plan active')
              : t('views.accountSettings.billing.currentPlan', 'Current plan')
            : card.packId
              ? card.packId === 'pack-monthly'
                ? t('views.pricing.subscribeMonthly', 'Subscribe Monthly')
                : card.packId === 'pack-quarterly'
                  ? t('views.pricing.subscribeQuarterly', 'Subscribe Quarterly')
                  : t('views.pricing.subscribeHalfYearly', 'Subscribe Half-Yearly')
              : t('views.pricing.planFree', 'Free');

          return (
            <article
              key={card.id}
              className={`billing-plan-card${isCurrent ? ' billing-plan-card--current' : ''}`}
            >
              <div className={`billing-plan-icon ${card.accentClass}`}>
                <FeatureIcon className="w-5 h-5" />
              </div>
              <div className="billing-plan-name-row">
                <p className="billing-plan-name">{card.name}</p>
                {card.badge && !isCurrent && (
                  <span className="billing-plan-badge">{card.badge}</span>
                )}
              </div>
              <p className="billing-plan-price">
                {card.priceLine(pack)}
                {card.periodLine && (
                  <span className="billing-plan-price-period"> / {card.periodLine}</span>
                )}
              </p>
              <p className="billing-plan-desc">{card.description}</p>
              <ul className="billing-plan-features">
                {card.features.map((f) => (
                  <li key={f.label}>
                    <f.Icon />
                    <span>{f.label}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={`billing-plan-cta ds-btn w-full${isCurrent ? ' ds-btn-secondary' : ' ds-btn-primary'}`}
                disabled={isCurrent || !card.packId}
                onClick={() => card.packId && onSelectPlan(card.packId)}
              >
                {ctaLabel}
              </button>
            </article>
          );
        })}
      </div>
      <p className="t-meta billing-plans-footer">
        {t('views.accountSettings.billing.footer', 'Secure payment via Cashfree · Cancel anytime')}
      </p>
    </section>
  );
}

/**
 * Billing tab — Wrangle-style plans picker only (no current-plan summary block).
 */
const Subscription: React.FC = () => {
  const { t } = useTranslation();
  const cards = useBillingPlanCards();
  const native = Capacitor.isNativePlatform();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [checkoutPackId, setCheckoutPackId] = useState<string | undefined>();
  const planStatus = usePlanStatus();

  useEffect(() => {
    if (native) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') === 'purchase') {
      goToCreditCheckout();
      window.history.replaceState({}, '', window.location.pathname || '/account-settings');
    }
  }, [native]);

  const handleSelectPlan = (packId: string) => {
    if (native) {
      setCheckoutPackId(packId);
      setShowPurchaseModal(true);
      return;
    }
    goToCreditCheckout(packId);
  };

  const isPaid = planStatus.isActive && planStatus.planKey !== 'free';

  if (native && !isPaid) {
    return (
      <div className="ds-page flex flex-col gap-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <section
          className="ds-card-pop text-center space-y-4"
          style={{
            background: 'linear-gradient(135deg, var(--ds-brand), var(--ds-brand-active))',
            color: 'var(--ds-text-on-brand)',
          }}
        >
          <div
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl"
            style={{ background: 'var(--ds-brand-soft-2)' }}
          >
            <Lock className="h-9 w-9" />
          </div>
          <div>
            <h1 className="t-hero" style={{ color: 'var(--ds-text-on-brand)' }}>
              {planStatus.isOutOfCredits
                ? t('views.accountSettings.billing.outOfCredits', "You're out of credits")
                : t('views.accountSettings.billing.unlockPremium', 'Unlock Premium Access')}
            </h1>
            <p className="t-meta mx-auto mt-2 max-w-sm" style={{ color: 'var(--ds-text-on-brand)' }}>
              {planStatus.isOutOfCredits
                ? t(
                    'views.accountSettings.billing.outOfCreditsDesc',
                    'Upgrade to keep optimising resumes and land more interviews faster.',
                  )
                : t(
                    'views.accountSettings.billing.unlockPremiumDesc',
                    'Get a plan to access AI-powered resume optimisation, interview prep and more.',
                  )}
            </p>
          </div>
        </section>

        <div className="px-4">
          <BillingPlanCards planStatus={planStatus} onSelectPlan={handleSelectPlan} cards={cards} />
        </div>

        <CreditPurchaseModal
          isOpen={showPurchaseModal}
          initialPackId={checkoutPackId}
          onClose={() => setShowPurchaseModal(false)}
          onPurchaseComplete={() => setShowPurchaseModal(false)}
        />
      </div>
    );
  }

  return (
    <>
      <BillingPlanCards planStatus={planStatus} onSelectPlan={handleSelectPlan} cards={cards} />

      {native && (
        <CreditPurchaseModal
          isOpen={showPurchaseModal}
          initialPackId={checkoutPackId}
          onClose={() => setShowPurchaseModal(false)}
          onPurchaseComplete={() => {
            setShowPurchaseModal(false);
            planStatus.refresh();
          }}
        />
      )}
    </>
  );
};

export default React.memo(Subscription);
